import L from 'leaflet';
import { agregarSuperposicion } from './controlDeCapas';
import type { Ciclon } from '../services/weather';

/**
 * Ciclones tropicales sobre el mapa: posición, cono y trayectoria.
 *
 * El cono no es "por dónde va a pasar" sino por dónde podría pasar el
 * CENTRO, y sólo dos de cada tres veces. Es el dato que más se malinterpreta
 * de un pronóstico de huracán, así que el dibujo lo dice con todas sus
 * letras en la etiqueta en vez de dejarlo a la intuición: los daños se
 * salen del cono con frecuencia, sobre todo la lluvia.
 */

/** Encima del radar y del satélite: es lo más importante si está en pantalla. */
const Z_INDEX = 450;

/**
 * Nudos a partir de los cuales cada categoría.
 *
 * Es la escala Saffir-Simpson en su umbral de huracán (64 kt) y el de
 * tormenta tropical (34 kt). Se usan colores del sistema y no los de una
 * escala meteorológica para que la pantalla siga siendo la misma pantalla.
 */
const HURACAN_KT = 64;
const TORMENTA_KT = 34;

function colorDe(vientoNudos: number): string {
  if (vientoNudos >= HURACAN_KT) return 'var(--red)';
  if (vientoNudos >= TORMENTA_KT) return 'var(--amber)';
  return 'var(--gray)';
}

/** Los anillos llegan en [lng, lat] (GeoJSON) y Leaflet quiere [lat, lng]. */
const aLatLng = (anillo: Array<[number, number]>): L.LatLngExpression[] =>
  anillo.map(([lng, lat]) => [lat, lng]);

export interface TextosCiclon {
  etiqueta: string;
  /** Arma la ficha del ciclón, ya traducida. */
  ficha: (c: Ciclon) => string;
  conoAviso: string;
}

/**
 * Dibuja los ciclones y devuelve la limpieza.
 *
 * Se redibuja entero cuando cambia la lista, en vez de ir comparando qué
 * tormenta se movió: son tres o cuatro objetos en el peor día del año y el
 * código de sincronizar sería más largo que el de dibujar.
 */
export function agregarCapaCiclones(
  map: L.Map,
  ciclones: Ciclon[],
  textos: TextosCiclon,
): () => void {
  const grupo = L.layerGroup();

  for (const c of ciclones) {
    const color = colorDe(c.vientoNudos);

    for (const anillo of c.cono) {
      L.polygon(aLatLng(anillo), {
        color,
        weight: 1,
        opacity: 0.7,
        fillColor: color,
        fillOpacity: 0.12,
        interactive: true,
      })
        .bindTooltip(textos.conoAviso, { sticky: true })
        .addTo(grupo);
    }

    for (const linea of c.trayectoria) {
      L.polyline(aLatLng(linea), {
        color,
        weight: 2,
        opacity: 0.9,
        dashArray: '6,5',
      }).addTo(grupo);
    }

    // El centro va como dos círculos concéntricos: uno relleno y uno hueco
    // más grande. Un ícono de imagen se vería torcido al alejarse el mapa,
    // y un emoji no se puede colorear por intensidad.
    L.circleMarker([c.lat, c.lng], {
      radius: 9,
      color,
      weight: 2,
      fillColor: color,
      fillOpacity: 0.25,
    }).addTo(grupo);
    L.circleMarker([c.lat, c.lng], {
      radius: 3,
      color,
      weight: 2,
      fillColor: color,
      fillOpacity: 1,
    })
      .bindTooltip(textos.ficha(c), { direction: 'top', offset: [0, -8] })
      .addTo(grupo);
  }

  // Sin ciclones activos no se ofrece la casilla: una capa que se prende y
  // no dibuja nada se siente rota. En temporada baja simplemente no está.
  if (ciclones.length === 0) return () => {};

  grupo.addTo(map);
  // Sube los vectores por encima de los mosaicos de radar y satélite.
  grupo.setZIndex?.(Z_INDEX);

  const quitar = agregarSuperposicion(map, grupo, textos.etiqueta);
  return () => quitar();
}
